import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Zap, Target, BarChart3, Users, Mail, MessageSquare, Globe, Linkedin, Database, Eye, RefreshCw, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const stats = [
  { value: '280M+', label: 'US based consumers in our database' },
  { value: '95%', label: 'Accuracy on fully verified contact data' },
  { value: '60B+', label: 'Behaviors and URLs scanned every week' }
];

const features = [
  {
    icon: Target,
    title: 'Unlimited Audiences',
    description: 'Harnessing the power of 60+ billion behaviors tracked beyond just social interactions, we are not restricted to the targeting options of traditional ad platforms.'
  },
  {
    icon: Globe,
    title: '360 Omni Channel Activation',
    description: 'With the ability to see a complete customer profile—from online to offline and B2B to B2C—we launch omni-channel campaigns across any platform.'
  },
  {
    icon: BarChart3,
    title: 'Analytics & Insights',
    description: 'Sales outreach on autopilot 24/7 with real-time data, analytics, and insights to optimize every campaign.'
  },
  {
    icon: Bot,
    title: 'The Best AI Agents',
    description: 'World-class AI technology will engage, nurture, and qualify your leads for you automatically.'
  }
];

const audienceTools = [
  {
    title: '25K Pre-Made Behavioral Audiences',
    description: 'Over 26,000 pre-made audiences that update every 24 hours. These are plug and play across both consumer and business services/products.',
    example: 'Our client Air.ai was able to use the pre-made audience "conversational ai" which tracks any businesses actively searching open ai tools and voice models.'
  },
  {
    title: 'Custom Models',
    description: 'We can create any models by creating an "audience prompt" to analyze over 60 billion competitor URLs.',
    example: 'A client wanted to target medical professionals willing to spend up to $40,000 for a medical device. We produced a list that converted 600+ qualified leads.'
  },
  {
    title: 'B2B Combinations',
    description: 'Our 280M consumer profiles have been tied directly to businesses. This powerful combination targeting allows us to find consumers and businesses looking for certain products/services.',
    example: 'A client needed to identify home service businesses who were actively searching for AI marketing technologies. They were able to produce leads for as low as $2.50.'
  },
  {
    title: 'Super Pixels',
    description: 'This identity resolution pixel matches anonymous website visitors to our consumer database of 280 million people, enabling identification of 20–60% of U.S.-based traffic.',
    example: 'We installed the Super Pixel on a client\'s website, and booked 2-3x more appointments from existing traffic.'
  }
];

const services = [
  {
    icon: Globe,
    title: 'Meta, Google & TikTok Ads',
    description: 'We launch high-converting ads on Meta, Google, and TikTok. From creatives to targeting, we handle it all.'
  },
  {
    icon: Bot,
    title: 'AI Outreach on Autopilot',
    description: 'Let our AI agents turn leads into booked deals. Your custom-built AI agent can handle both outbound and inbound calls and texts.'
  },
  {
    icon: Mail,
    title: 'Multi-Channel Campaigns',
    description: 'Reach your high-intent audience with AI-powered emails, voice, text, hyper-targeted ads, and exclusive LinkedIn outreach.'
  },
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    description: 'Track performance live, know what\'s working, get instant alerts, and let AI fine-tune every step for maximum ROI.'
  },
  {
    icon: Target,
    title: 'Intelligent Lead Targeting',
    description: 'Use behavioral data to spot high-intent leads, segment by actions, send personalized messages, and integrate with your CRM.'
  },
  {
    icon: Linkedin,
    title: 'LinkedIn Outreach System',
    description: 'Send up to 200 direct messages daily. With a dedicated team, powerful targeting, and optimized scripts, we turn LinkedIn into a revenue channel.'
  }
];

const testimonials = [
  {
    quote: "The high intent data was a total game changer for us. Being able to find customers who are actually looking for our services was huge, but then the outreach was incredible.",
    author: "James S."
  },
  {
    quote: "We have already tripled our ROI in just 2 months!",
    author: "Meghan T."
  },
  {
    quote: "I love how everything is done for you, I don't have to waste my time trying to figure all this out! I get constant booked calls and my pipeline stays full!",
    author: "Terrence W."
  },
  {
    quote: "This system blew me away. I was very skeptical but leads and qualified appointments started coming in from week 1! Can't recommend them enough!",
    author: "Juan D."
  }
];

const caseStudies = [
  {
    id: 1,
    title: 'Medical Technology',
    subtitle: '$24M Pipeline Generated',
    challenge: 'Client sold a high-ticket medical device ($40,000+) and struggled to find ideal customers on Facebook ads as their target was strictly medical professionals.',
    solution: 'Used custom models to identify medical professionals actively searching for diagnostic equipment. Data synced directly to outbound B2B team.',
    results: ['600+ quality leads from 60,000 profiles', '$24M+ in pipeline value', 'Completed in just 3 days'],
    metric: '$24M',
    metricLabel: 'Pipeline Value'
  },
  {
    id: 2,
    title: 'StickerYou (E-commerce)',
    subtitle: '$200K Revenue in 90 Days',
    challenge: 'Using a competing platform for identity resolution with far lower match rates.',
    solution: 'Deployed SuperPixel achieving 60% match rate (20% higher than competitor). Created hyper-targeted Meta ads and AI texts.',
    results: ['80% open rate (up from 40%)', '500 new customers generated', '$200,000+ collected in 90 days'],
    metric: '$200K',
    metricLabel: 'Revenue in 90 Days'
  },
  {
    id: 3,
    title: 'Air.ai (SaaS)',
    subtitle: '$11M Revenue in 30 Days',
    challenge: 'Needed to target specific industries on Facebook Ads with both intent and qualifications for 12-month subscriptions.',
    solution: 'Used B2B2C feature to identify agencies over certain revenue actively searching for AI technologies. Synced directly to Facebook with fresh daily data.',
    results: ['$11M revenue in 30 days', '50% higher lead quality', 'Lower cost per show up'],
    metric: '$11M',
    metricLabel: 'Revenue in 30 Days'
  },
  {
    id: 4,
    title: 'Auto Group',
    subtitle: '35% Increase in Monthly Sales',
    challenge: 'Relied on traditional marketing (radio, billboards) and generic Facebook ads with low results.',
    solution: 'Generated 1,608 leads in 3 days with credit scores, auto mileage, car make, and in-market affinity data.',
    results: ['1,608 leads in 3 days', '35% increase in monthly sales', 'Highly qualified data'],
    metric: '35%',
    metricLabel: 'Sales Increase'
  },
  {
    id: 5,
    title: 'HVAC Company',
    subtitle: '4 Sales in First Week',
    challenge: 'No results from Facebook lookalike audiences and SEO efforts.',
    solution: 'Identified individuals actively searching for HVAC services and deployed targeted email outreach, then added Facebook ads.',
    results: ['4 sales in first week (no ad spend)', '40+ qualified leads per week', 'Zero to profitable quickly'],
    metric: '40+',
    metricLabel: 'Leads Per Week'
  },
  {
    id: 6,
    title: 'Financial Advisor',
    subtitle: '28 Qualified Appointments Monthly',
    challenge: 'Struggling with qualified appointments using Facebook ads, Google ads, and LinkedIn campaigns.',
    solution: 'Identified high-intent audiences searching for financial planning. Comprehensive outreach with AI emails, texts, voice, LinkedIn, and revamped ads.',
    results: ['28 qualified appointments/month', '70 leads per month', 'Results from week 1'],
    metric: '28',
    metricLabel: 'Monthly Appointments'
  },
  {
    id: 7,
    title: 'Solar Company',
    subtitle: '65 Booked Calls Monthly',
    challenge: 'Door-to-door sales difficult for residential, commercial outreach time-consuming. High ad spend with poor results.',
    solution: 'Sourced high-intent leads with separate campaigns for residential and commercial. AI agents qualified all responses.',
    results: ['65 booked calls per month', 'Both residential & commercial', 'Qualified interested prospects'],
    metric: '65',
    metricLabel: 'Monthly Calls'
  },
  {
    id: 8,
    title: 'Real Estate Agency',
    subtitle: '5-10 Qualified Leads Daily',
    challenge: 'Competing for attention on Facebook and Google Ads in crowded real estate space.',
    solution: 'Identified high-intent leads actively looking to buy/sell, filtered by credit score, home value, price. Launched hyper-targeted Meta ads.',
    results: ['5-10 qualified leads per day', 'Filtered by credit & home value', 'Complete sales funnel'],
    metric: '10',
    metricLabel: 'Daily Leads'
  }
];

const faqs = [
  {
    question: "Can this system work for high-ticket B2B products?",
    answer: "Absolutely. For example, we helped a medical device company selling $40K+ equipment generate over $24M in pipeline by identifying medical professionals actively searching for diagnostic tools—using custom AI models and audience prompts."
  },
  {
    question: "Can this system work for B2C?",
    answer: "Of course, our high intent data can also find individuals who are actively searching for consumer products and filter that individual by age, sex, location, income, credit scores, networth, and more."
  },
  {
    question: "How fast can we see results?",
    answer: "Results can vary. We've had many clients start to generate leads and qualified appointments within days, and others can take a month or so. The medical tech company mentioned above got 600+ qualified leads from a single list in just 3 days."
  },
  {
    question: "How accurate is your identity resolution?",
    answer: "With tools like our SuperPixel, we achieve up to 60% match rates, outperforming competitors by 20%. This allows for high open rates (80%+) and more conversions from existing traffic."
  },
  {
    question: "What makes your data different or better?",
    answer: "We use O2O (Online to Offline) which provides a 360° view of a person by linking their online and offline identities. We cross-check with verified offline sources—like census records, real estate data, and financial databases. This dual-layer validation boosts accuracy to over 90%."
  },
  {
    question: "What platforms do you integrate with?",
    answer: "We sync data directly with major ad platforms like Facebook (via real-time sync), outbound tools, CRMs, and more. This ensures fresh, intent-based data fuels your campaigns daily."
  }
];

const LeadGeneration = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-60"
              style={{
                background: `
                  radial-gradient(ellipse 80% 60% at 70% 20%, rgba(100, 200, 255, 0.4) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 80% 50%, rgba(180, 100, 255, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 60% 70%, rgba(100, 255, 200, 0.3) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Lead Generation</span>
              <h1 className="text-[clamp(2.5rem,6vw,5rem)] font-light leading-[1.1] text-black mb-8">
                Let data lead<br />
                <span className="font-medium">the way.</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-8">
                Discover who's looking for your product or service this second and be the first to reach out. 
                Our complete lead generation system will reach out, nurture, and qualify your leads for you.
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
              >
                Get Started <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center md:text-left">
                  <div className="text-4xl md:text-5xl font-light mb-2">{stat.value}</div>
                  <p className="text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Zero in on your dream customers like never before
              </h2>
              <p className="text-lg text-gray-600">
                Gain an unfair advantage with our cutting-edge lead generation system. Leverage real-time intent data 
                to identify prospects actively searching for your products or services—updated every 24 hours for maximum accuracy.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <feature.icon className="w-10 h-10 text-black mb-4" />
                  <h3 className="text-xl font-medium text-black mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audience Intelligence Section */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Unlock Powerful Audience Intelligence
              </h2>
              <p className="text-lg text-gray-600">
                Leverage over 25,000 pre-made behavioral audiences that update daily, custom AI-driven models, 
                and seamless B2B consumer targeting—all powered by a massive database of 280 million profiles.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {audienceTools.map((tool, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-colors ${
                    activeTab === index
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tool.title.split(' ').slice(0, 2).join(' ')}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
              <h3 className="text-2xl font-medium text-black mb-4">
                {audienceTools[activeTab].title}
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                {audienceTools[activeTab].description}
              </p>
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <span className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Example</span>
                <p className="text-gray-700">{audienceTools[activeTab].example}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Done-For-You Outreach, Built to Convert at Scale
              </h2>
              <p className="text-lg text-gray-600">
                Finding your audience is just the start. Our complete system delivers fresh, qualified leads daily—or 
                runs full outreach campaigns for you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all group cursor-pointer">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-black mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium mb-6">
                How It Works
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Find Your Ideal Audience', desc: 'Tap into real, verified data to identify high-value prospects instantly.' },
                { step: '02', title: 'Fresh Leads Daily', desc: 'Get updated, qualified contacts sent directly to your CRM—ready to act.' },
                { step: '03', title: 'Automated Outreach', desc: 'AI-powered emails, texts, ads, and DMs—handled for you, start to finish.' },
                { step: '04', title: 'Close More Deals', desc: 'We build full sales funnels and creatives tailored to your exact audience.' }
              ].map((item, index) => (
                <div key={index}>
                  <span className="text-gray-600 text-sm font-medium">{item.step}</span>
                  <h3 className="text-xl font-medium mt-2 mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Case Studies
              </h2>
              <p className="text-lg text-gray-600">
                Real results from real clients. See how businesses across industries have transformed their 
                lead generation with our data-driven approach.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((study) => (
                <div key={study.id} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="text-xs uppercase tracking-wider text-gray-500">{study.title}</span>
                      <h3 className="text-xl font-medium text-black mt-1">{study.subtitle}</h3>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-light text-black">{study.metric}</span>
                      <p className="text-xs text-gray-500">{study.metricLabel}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="text-xs font-medium text-gray-400 uppercase">Challenge</span>
                      <p className="text-sm text-gray-600 mt-1">{study.challenge}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-400 uppercase">Solution</span>
                      <p className="text-sm text-gray-600 mt-1">{study.solution}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <span className="text-xs font-medium text-gray-400 uppercase">Results</span>
                    <ul className="mt-2 space-y-1">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check size={14} className="text-green-500 flex-shrink-0" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
              What Our Customers Say
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                  <blockquote className="text-lg text-gray-700 mb-4">
                    "{item.quote}"
                  </blockquote>
                  <p className="font-medium text-black">— {item.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Frequently Asked Questions
              </h2>

              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border border-gray-200 rounded-xl px-6 data-[state=open]:bg-gray-50"
                  >
                    <AccordionTrigger className="text-left text-lg font-medium hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to turbocharge<br />
                <span className="font-medium">your sales on autopilot?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Stop guessing. Start targeting. With real-time search tracking, you can see exactly who's hunting 
                for what you offer—reach them while they're searching.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:hello@gomobyle.com"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors"
                >
                  Book a Consultation <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default LeadGeneration;
